/**
 * Promo slug capture + persistence.
 *
 * A visitor lands via `?promo=iu` (or any future slug). We:
 *   1. Read the slug once on page load via captureFromUrl()
 *   2. Persist to localStorage under PROMO_STORAGE_KEY (survives OAuth
 *      redirects through Google → Supabase → back to our origin, which
 *      sessionStorage cannot reliably do across all browsers).
 *   3. Every checkout call reads getPromoSlug() and forwards it to the
 *      create-checkout-session edge function as `promoSlug`, where a
 *      server-side whitelist maps it to a Stripe promotion_code ID.
 *   4. clearPromoSlug() should only be called after a successful checkout.
 *
 * Slugs are lower-cased + trimmed. Unknown slugs are silently ignored by the
 * edge function, so we don't need client-side validation.
 */

const PROMO_STORAGE_KEY = 'vovy_promo_slug';
const PROMO_QUERY_PARAM = 'promo';

function safeGet(storage: Storage | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: Storage | null, key: string, value: string): void {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(storage: Storage | null, key: string): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function captureFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get(PROMO_QUERY_PARAM);
    if (!raw) return getPromoSlug();
    const slug = raw.toLowerCase().trim();
    if (!slug) return getPromoSlug();
    // Persist to BOTH local + session so we survive OAuth redirects (which
    // can land in a context where sessionStorage was cleared) and also keep
    // the legacy session entry for any code still reading it.
    safeSet(window.localStorage, PROMO_STORAGE_KEY, slug);
    safeSet(window.sessionStorage, PROMO_STORAGE_KEY, slug);
    return slug;
  } catch {
    return getPromoSlug();
  }
}

export function getPromoSlug(): string | null {
  if (typeof window === 'undefined') return null;
  // Prefer localStorage (persists across OAuth redirects), fall back to
  // sessionStorage for any value written by older builds.
  return (
    safeGet(window.localStorage, PROMO_STORAGE_KEY) ||
    safeGet(window.sessionStorage, PROMO_STORAGE_KEY)
  );
}

export function clearPromoSlug(): void {
  if (typeof window === 'undefined') return;
  safeRemove(window.localStorage, PROMO_STORAGE_KEY);
  safeRemove(window.sessionStorage, PROMO_STORAGE_KEY);
}
